import { fromJS, List, Map } from 'immutable';
import { dispatch, pathSubscription } from 'nike-re-framejs';
import { CHAT_ALL, CHAT_CLOSED, CHAT_MAXIMIZED, CHAT_OPEN, CHAT_TYPES, MAX_LENGTH } from '@/forum/chat/constants';
import forumClient from '@/_http/axiosForumClient';
import { navigate } from '@/routing';
import { COMMUNITY_CHAT } from '@/routing/routes';
import { addAnalyseToTopic } from '@/forum/ticketpicker/handlersAndSubs';

////////// utilities

const initialState = fromJS({
    position: CHAT_CLOSED,
    tickets: [],
    messages: { byId: [], allIds: [] },
    topic: null,
    replyToMessage: null,
    subscriptionType: CHAT_ALL
});

const DEFAULT_TOPIC = { subjectType: 'CHAT', subjectId: 'Chat' };

const replaceConsecutiveNewLines = msg => msg.replace(/[\n]+/g, '\n');

const normalizeChatMessages = messages => messages.reduce(chatMessages, Map({ byId: Map(), allIds: List() }));

const chatMessages = (acc, item) => {
    return acc.setIn(['byId', item['id']], fromJS(item)).updateIn(['allIds'], old => old.concat(item['id']));
};

//////// API

export const validateAnalysis = (topic, id, postDetail) => {
    return forumClient.get(`/v5/betbook/analyses/${id}/can-share-analysis`).then(analysis => {
        if (analysis) {
            dispatch(chatSetUsedAnalysis({ analysis, id }));
        }
        if (analysis.canShareAnalysis === true) {
            dispatch(addAnalyseToTopic(topic, postDetail));
        }
    });
};

export const sendMessage = (msg, avatarId = 1, parentCommentId) => {
    const text = replaceConsecutiveNewLines(msg);
    const comment = { text, device: 'MOBILE', avatarId, parentCommentId };

    return forumClient
        .post(`/v5/topics/${DEFAULT_TOPIC.subjectType}/${DEFAULT_TOPIC.subjectId}/comments`, comment)
        .then(() => dispatch(chatSetError(false)))
        .then(() => navigate(COMMUNITY_CHAT))
        .catch(e => dispatch(chatSetError(e.response.data)));
};

export const getAnalysisSharedPost = (subjectType, subjectId, analysisId) => {
    const params = { analysisId };
    return forumClient
        .get(`/v5/topic/${subjectType}/${subjectId}/comment-sharing-analysis`, { params })
        .then(item => dispatch(analysisSharedPost(item)));
};

////////// subscription

export const chatAddMessage = message => db => {
    let ret = db
        .setIn(['chat', 'messages', 'byId', message.id], fromJS(message))
        .updateIn(['chat', 'messages', 'byId'], messageMap =>
            messageMap.map(v =>
                v.get('parentCommentId') === message.id ? v.set('parentCommentStatus', message.status) : v
            )
        );
    if (!db.getIn(['chat', 'messages', 'byId', message.id])) {
        ret = ret.updateIn(['chat', 'messages', 'allIds'], messages => {
            if (db.getIn(['chat', 'messages', 'byId', messages.first(), 'commentTime']) > message.commentTime) {
                return messages.unshift(message.id);
            } else {
                return messages.concat(message.id);
            }
        });
    }
    return ret;
};

export const chatAddSnapshot = snapshot => db =>
    db
        .setIn(['chat', 'topic'], fromJS(snapshot.topic))
        .setIn(['chat', 'messages'], normalizeChatMessages(snapshot.messages.reverse()));

export const chatSetSubscriptionType = type => db =>
    db.update('chat', initialState, state => {
        if (CHAT_TYPES.includes(type)) {
            return initialState.set('position', state.get('position')).set('subscriptionType', type);
        }
        return db;
    });

export const chatMessages$ = pathSubscription(['chat', 'messages', 'allIds']);
export const chatMessageById$ = messageId => pathSubscription(['chat', 'messages', 'byId', messageId]);
export const chatSubscriptionType$ = pathSubscription(['chat', 'subscriptionType'], CHAT_ALL);
export const chatOpenedTicketFromChat$ = pathSubscription(['chat', 'openedTicketFromChat']);

////////// sending messages

export const checkMessage = (message, maxLength = MAX_LENGTH) => {
    // too long or too short or too white space-y
    if (message.length > maxLength || message.trim().leading === 0) {
        return false;
    }
    // 6 same consecutive chars like gooooool
    return !/(.)(?=\1{5,})/gi.test(message);
};

export const chatSendMessage = (message, parentCommentId) => db => {
    if (!db.get('loggedIn')) {
        return;
    }
    const avatarId = db.getIn(['account', 'userData', 'avatar']);
    // noinspection JSIgnoredPromiseFromCall
    sendMessage(message, avatarId, parentCommentId);
    return db;
};

/////////// save message text
export const saveMessageText = text => db => db.setIn(['save-chat-message'], text);

////////// chat position
export const chatSetPosition = position => db =>
    db.update('chat', initialState, state => {
        const currentPosition = state.get('position');
        switch (position) {
            case CHAT_CLOSED:
            case CHAT_OPEN:
                return state.set('position', position);
            case CHAT_MAXIMIZED:
                if (currentPosition === CHAT_OPEN) {
                    return state.set('position', position);
                }
                return state;
            default:
                return state;
        }
    });

///setTicketFromChat
export const setTicketHashOpenFromChat = hash => db => db.setIn(['chat', 'openedTicketFromChat'], hash);
export const clearTicketHashOpenFromChat = db => db.deleteIn(['chat', 'openedTicketFromChat']);
//chat errors
export const chatSetError = errorState => db =>
    db.update('chat', initialState, state => state.set('error', errorState));

export const chatSetUsedAnalysis = canShareAnalysis => db =>
    db.setIn(['forum', 'can-share-analysis'], canShareAnalysis);
export const analysisSharedPost = post => db => db.setIn(['forum', 'shared-analysis-post'], post);

export const chatError$ = pathSubscription(['chat', 'error']);
export const chatErrorCanShareAnalysis$ = pathSubscription(['forum', 'can-share-analysis']);
export const sharedAnalysisPost$ = pathSubscription(['forum', 'shared-analysis-post']);
export const loadSavedChatMessage$ = pathSubscription(['save-chat-message']);
